import { MosError } from 'src/utils/errors/MosError';

/**
 * Handles HTTP 400 errors specifically for income calculation services
 * @param error - The MosError instance
 * @returns The actual error message from the API
 */
export const handleIncomeCalculation400Error = (error: MosError): string => {
  console.log('handleIncomeCalculation400Error received:', error.message);
  // Just return the actual error message from the API
  return error.message;
};

/**
 * Generic error handler for income services
 * @param error - The error to handle
 * @throws Error with user-friendly message
 */
export const handleIncomeServiceError = (error: unknown): never => {
  if (error instanceof MosError && error.statusCode === 400) {
    throw new Error(handleIncomeCalculation400Error(error));
  }

  // Re-throw other errors as-is
  throw error;
};

/**
 * Validates API response data for income services
 * @param data - The response data to validate
 * @param errorMessage - Custom error message if validation fails
 * @throws Error if data is invalid
 */
export const validateIncomeResponse = (
  data: unknown,
  errorMessage: string,
): void => {
  if (!data) {
    throw new Error(errorMessage);
  }
};

/**
 * Validates API response success for income services
 * @param response - The API response
 * @param errorMessage - Custom error message if not successful
 * @throws Error if response is not successful
 */
export const validateIncomeSuccess = (
  response: { success?: boolean; error?: { message?: string } },
  errorMessage: string,
): void => {
  if (!response.success) {
    if (response.error?.message) {
      throw new Error(response.error.message);
    }
    throw new Error(errorMessage);
  }
};

/**
 * Handles 404 errors for income-clarity endpoints by returning empty state
 * @param error - The MosError instance
 * @returns Empty income response structure
 */
export const handleIncomeClarity404Error = (error: MosError) => {
  console.log('Income clarity 404 - treating as empty state:', error.message);
  return {
    success: true,
    data: {
      files: [],
      transactionId: '',
      status: 'not_started',
      income_clarity_lines: [],
      income_clarity_alerts: [],
    },
  };
};
